import {
  act,
  fireEvent,
  render,
  renderHook,
  screen,
  waitFor,
} from '@testing-library/react'
import { type FieldValue } from 'features/libra/ontology/ontologyFieldTypes'
import { beforeEach, describe, expect, it, vi } from 'vitest'

const postMock = vi.fn()
const runtimeVarsMock = vi.fn()
const ticketAccessMock = vi.fn()
const docProcessingMock = vi.fn()
const reprocessMock = vi.fn()
const blockedReasonMock = vi.fn<() => string | null>(() => null)

vi.mock('app-kit', () => ({
  useRuntimeVariables: () => runtimeVarsMock(),
}))

vi.mock('features/auth', () => ({
  useGeckoAuth: () => ({ authorizedAxios: { post: postMock } }),
}))

vi.mock('features/libra/ticket/TicketAccessContext', () => ({
  useTicketAccess: () => ticketAccessMock(),
}))

vi.mock('features/libra/stepComponents/prefillGate', () => ({
  usePrefillBlockedReason: () => blockedReasonMock(),
}))

vi.mock('features/libra/document/useDocumentProcessingReady', () => ({
  useDocumentProcessingReady: () => docProcessingMock(),
}))

vi.mock('features/rolodex', () => ({
  useReprocessDocuments: () => reprocessMock,
}))

vi.mock('features/permissions', () => ({
  PermissionsGate: ({ children }: { children: React.ReactNode }) => children,
}))

vi.mock('features/libra/document/DocumentDataContext', async () => {
  const { createContext } = await import('react')
  return { DocumentDataContext: createContext(undefined) }
})

vi.mock('features/libra/document/LibraDocumentContext', async () => {
  const { createContext } = await import('react')
  return { LibraDocumentContext: createContext(undefined) }
})

import {
  AiAutofillBanner,
  AiCitationChip,
  useGongAgentAutofill,
} from './GongAgentAutofill'

describe('useGongAgentAutofill', () => {
  beforeEach(() => {
    postMock.mockReset()
    runtimeVarsMock.mockReset()
    ticketAccessMock.mockReset()
    runtimeVarsMock.mockReturnValue({ GONG_AGENT_URL: 'http://fake' })
    ticketAccessMock.mockReturnValue({ ticketId: 't1' })
    blockedReasonMock.mockReturnValue(null)
  })

  it('marks every returned wrapper llm, including a not-found result', async () => {
    postMock.mockResolvedValue({
      data: {
        field_values: {
          found: { value: 'x' },
          missing: { value: null },
        },
        documents_used: ['doc-1'],
      },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    const arg = onFieldValues.mock.calls[0][0]
    expect(arg.found.updateSource).toBe('llm')
    expect(arg.missing).toMatchObject({
      updateSource: 'llm',
      aiExtracted: false,
      documentsSearched: ['doc-1'],
    })
  })

  it('posts to /api/v1/assess/section with ticket_id and section_id', async () => {
    postMock.mockResolvedValue({
      data: { field_values: {}, documents_used: [] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    expect(postMock).toHaveBeenCalledTimes(1)
    expect(postMock).toHaveBeenCalledWith('http://fake/api/v1/assess/section', {
      ticket_id: 't1',
      section_id: 's1',
    })
  })

  it('forwards inputs verbatim in the POST body when provided (zone suggestion)', async () => {
    postMock.mockResolvedValue({
      data: { field_values: {}, documents_used: [] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({
        sectionId: 'zone_analysis_section',
        onFieldValues,
        inputs: { zone_reference_image_id: 'img-1' },
      })
    )
    await act(async () => {
      await result.current.autofill()
    })
    expect(postMock).toHaveBeenCalledWith('http://fake/api/v1/assess/section', {
      ticket_id: 't1',
      section_id: 'zone_analysis_section',
      inputs: { zone_reference_image_id: 'img-1' },
    })
  })

  it('passes the server field_values straight through to onFieldValues, with llmValue riding along', async () => {
    const serverFieldValues = {
      inspectorName: {
        value: 'Alice',
        source: null,
        llmValue: 'Alice',
      },
      severity: {
        value: 'high',
        source: { type: 'document', value: 'doc-1' },
        llmValue: 'high',
      },
      legacyFieldNoLlmValue: {
        value: 42,
        source: null,
      },
    }
    postMock.mockResolvedValue({
      data: { field_values: serverFieldValues, documents_used: ['doc-1'] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    expect(onFieldValues).toHaveBeenCalledTimes(1)
    const arg = onFieldValues.mock.calls[0][0]
    expect(arg).toEqual(
      Object.fromEntries(
        Object.entries(serverFieldValues).map(([key, wrapper]) => [
          key,
          { ...wrapper, updateSource: 'llm' },
        ])
      )
    )
    expect(arg.inspectorName.llmValue).toBe('Alice')
    expect(arg.severity.llmValue).toBe('high')
    expect('llmValue' in arg.legacyFieldNoLlmValue).toBe(false)
  })

  it('sets error from err.response.data.detail and does not call onFieldValues on failure', async () => {
    postMock.mockRejectedValue({
      response: { data: { detail: 'boom' } },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    await waitFor(() => {
      expect(result.current.error).toBe('boom')
    })
    expect(onFieldValues).not.toHaveBeenCalled()
  })

  it('falls back to a generic error message when detail is absent', async () => {
    postMock.mockRejectedValue(new Error('network down'))
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    await waitFor(() => {
      expect(result.current.error).toBe('AI pre-fill failed')
    })
    expect(onFieldValues).not.toHaveBeenCalled()
  })

  it('does not POST when GONG_AGENT_URL is missing and sets a config error', async () => {
    runtimeVarsMock.mockReturnValue({ GONG_AGENT_URL: '' })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    expect(postMock).not.toHaveBeenCalled()
    expect(result.current.error).toBe('GONG_AGENT_URL is not configured')
    expect(onFieldValues).not.toHaveBeenCalled()
  })

  it('captures documents_used from the response', async () => {
    postMock.mockResolvedValue({
      data: { field_values: {}, documents_used: ['doc-a', 'doc-b'] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    await waitFor(() => {
      expect(result.current.documentsUsed).toEqual(['doc-a', 'doc-b'])
    })
  })

  it('stamps stable _id on array-row values missing one before handing them to onFieldValues', async () => {
    const serverFieldValues = {
      defects: {
        value: [{ note: 'crack A' }, { note: 'crack B' }],
        source: null,
        llmValue: [{ note: 'crack A' }, { note: 'crack B' }],
      },
    }
    postMock.mockResolvedValue({
      data: { field_values: serverFieldValues, documents_used: [] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    expect(onFieldValues).toHaveBeenCalledTimes(1)
    const arg = onFieldValues.mock.calls[0][0]
    const rows = arg.defects.value
    expect(rows).toHaveLength(2)
    expect(typeof rows[0]._id).toBe('string')
    expect(rows[0]._id.length).toBeGreaterThan(0)
    expect(typeof rows[1]._id).toBe('string')
    expect(rows[0]._id).not.toBe(rows[1]._id)
    expect(rows[0].note).toBe('crack A')
    expect(rows[1].note).toBe('crack B')
  })

  it('preserves wrapper content when every array-row already has an _id', async () => {
    const defectsWrapper = {
      value: [
        { _id: 'row-1', note: 'crack A' },
        { _id: 'row-2', note: 'crack B' },
      ],
      source: null,
      llmValue: [
        { _id: 'row-1', note: 'crack A' },
        { _id: 'row-2', note: 'crack B' },
      ],
    }
    const serverFieldValues = { defects: defectsWrapper }
    postMock.mockResolvedValue({
      data: { field_values: serverFieldValues, documents_used: [] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    const arg = onFieldValues.mock.calls[0][0]
    // Reference-equality to the raw axios payload is not preserved because
    // the ingestion path zod-parses the payload into a validated copy. What
    // matters here is that stampArrayRowIds is a no-op when every row is
    // already stamped, so content round-trips unchanged.
    expect(arg.defects).toEqual({ ...defectsWrapper, updateSource: 'llm' })
  })

  it('does not stamp _id onto rows inside llmValue (lineage is immutable, stringified only)', async () => {
    const serverFieldValues = {
      defects: {
        value: [{ note: 'crack A' }],
        source: null,
        llmValue: [{ note: 'crack A' }],
      },
    }
    postMock.mockResolvedValue({
      data: { field_values: serverFieldValues, documents_used: [] },
    })
    const onFieldValues = vi.fn()
    const { result } = renderHook(() =>
      useGongAgentAutofill({ sectionId: 's1', onFieldValues })
    )
    await act(async () => {
      await result.current.autofill()
    })
    const arg = onFieldValues.mock.calls[0][0]
    expect(arg.defects.value[0]._id).toBeDefined()
    expect(arg.defects.llmValue[0]._id).toBeUndefined()
  })
})

describe('AiCitationChip', () => {
  it('renders nothing (never crashes) for a citation with a null doc', () => {
    const fieldValue = {
      value: 'x',
      llmValue: { value: 'x', citation: { doc: null, page: null } },
    } as unknown as FieldValue

    const { container } = render(<AiCitationChip fieldValue={fieldValue} />)
    expect(container.firstChild).toBeNull()
  })

  it('still renders a chip for a real document citation', () => {
    const fieldValue = {
      value: 'x',
      llmValue: {
        value: 'x',
        citation: { doc: 'Inspection Report.pdf', page: 4 },
      },
    } as unknown as FieldValue

    const { container } = render(<AiCitationChip fieldValue={fieldValue} />)
    expect(container.textContent).toContain('Inspection Report.pdf')
  })
})

describe('AiAutofillBanner reprocess', () => {
  const readyState = {
    allProcessed: true,
    isCheckingProcessing: false,
    unprocessedCount: 0,
    failedEntryIds: [] as string[],
    refreshProcessingStatus: vi.fn(),
  }

  beforeEach(() => {
    reprocessMock.mockReset()
    docProcessingMock.mockReset()
    runtimeVarsMock.mockReset()
    ticketAccessMock.mockReset()
    runtimeVarsMock.mockReturnValue({ GONG_AGENT_URL: 'http://fake' })
    ticketAccessMock.mockReturnValue({ ticketId: 't1' })
    readyState.refreshProcessingStatus = vi.fn()
    docProcessingMock.mockReturnValue(readyState)
  })

  it('renders the "Reprocess failed" button only when there are failed documents', () => {
    docProcessingMock.mockReturnValue({ ...readyState, failedEntryIds: [] })
    const { rerender } = render(
      <AiAutofillBanner
        section={{ id: 's1' }}
        fieldValues={{}}
        fieldLabels={{}}
        onFieldValues={vi.fn()}
      />
    )
    expect(
      screen.queryByRole('button', { name: /reprocess failed/i })
    ).toBeNull()

    docProcessingMock.mockReturnValue({
      ...readyState,
      allProcessed: false,
      unprocessedCount: 2,
      failedEntryIds: ['e1', 'e2'],
    })
    rerender(
      <AiAutofillBanner
        section={{ id: 's1' }}
        fieldValues={{}}
        fieldLabels={{}}
        onFieldValues={vi.fn()}
      />
    )
    expect(
      screen.getByRole('button', { name: /reprocess failed/i })
    ).toBeTruthy()
  })

  it('reprocesses the failed entry ids and re-queries stats on success', async () => {
    reprocessMock.mockResolvedValue(undefined)
    docProcessingMock.mockReturnValue({
      ...readyState,
      allProcessed: false,
      unprocessedCount: 2,
      failedEntryIds: ['e1', 'e2'],
    })
    render(
      <AiAutofillBanner
        section={{ id: 's1' }}
        fieldValues={{}}
        fieldLabels={{}}
        onFieldValues={vi.fn()}
      />
    )

    await act(async () => {
      fireEvent.click(screen.getByRole('button', { name: /reprocess failed/i }))
    })

    expect(reprocessMock).toHaveBeenCalledWith(['e1', 'e2'])
    await waitFor(() =>
      expect(readyState.refreshProcessingStatus).toHaveBeenCalledTimes(1)
    )
  })

  it('shows a Rolodex-admin message on a 403 and does not re-query', async () => {
    reprocessMock.mockRejectedValue({ response: { status: 403 } })
    docProcessingMock.mockReturnValue({
      ...readyState,
      allProcessed: false,
      unprocessedCount: 1,
      failedEntryIds: ['e1'],
    })
    render(
      <AiAutofillBanner
        section={{ id: 's1' }}
        fieldValues={{}}
        fieldLabels={{}}
        onFieldValues={vi.fn()}
      />
    )

    await act(async () => {
      fireEvent.click(screen.getByRole('button', { name: /reprocess failed/i }))
    })

    await waitFor(() =>
      expect(
        screen.getByText(
          'You need Rolodex admin access to reprocess documents.'
        )
      ).toBeTruthy()
    )
    expect(readyState.refreshProcessingStatus).not.toHaveBeenCalled()
  })
})

describe('AiAutofillBanner disagreements', () => {
  const readyState = {
    allProcessed: true,
    isCheckingProcessing: false,
    unprocessedCount: 0,
    failedEntryIds: [] as string[],
    refreshProcessingStatus: vi.fn(),
  }

  beforeEach(() => {
    postMock.mockReset()
    runtimeVarsMock.mockReset()
    ticketAccessMock.mockReset()
    docProcessingMock.mockReset()
    runtimeVarsMock.mockReturnValue({ GONG_AGENT_URL: 'http://fake' })
    ticketAccessMock.mockReturnValue({ ticketId: 't1' })
    docProcessingMock.mockReturnValue(readyState)
    blockedReasonMock.mockReturnValue(null)
  })

  it('clears prior disagreement evidence when a rerun fails', async () => {
    postMock
      .mockResolvedValueOnce({
        data: {
          field_values: { human: { value: 'AI suggestion' } },
          documents_used: ['doc-1'],
        },
      })
      .mockRejectedValueOnce(new Error('network down'))

    render(
      <AiAutofillBanner
        section={{ id: 's1' }}
        fieldValues={{ human: { value: 'Human value', updateSource: 'human' } }}
        fieldLabels={{ human: 'Human field' }}
        onFieldValues={vi.fn()}
      />
    )
    await act(async () => {
      fireEvent.click(screen.getByRole('button', { name: /Pre-fill with AI/ }))
    })
    await waitFor(() => expect(screen.getByText('Human field')).toBeTruthy())

    await act(async () => {
      fireEvent.click(
        screen.getByRole('button', { name: /Pre-fill with AI again/ })
      )
    })
    await waitFor(() =>
      expect(screen.getByText('AI pre-fill failed')).toBeTruthy()
    )
    expect(screen.queryByText('Human field')).toBeNull()
  })
})
